import { updateRealtimeSignals } from "@main/features/session/state/realtime-detection-signals";
import { pushSessionAccumulators } from "@main/features/session/state/session-store";
import { type RafBindings, startFrameLoop } from "@main/infra/frame-loop";
import { handleAppError } from "@main/infra/handle-app-error";
import type { WorkerClient } from "@main/infra/worker-client";
import { isAppError } from "@shared/app-error";
import type { AnalyzeFrameResult } from "@shared/worker-protocol";
import type { Accessor } from "solid-js";
import {
  type DetectionInput,
  type DetectionStep,
  type DetectionTuning,
  step as detectionStep,
  INITIAL_STATE,
} from "./detection-machine";
import {
  assertSelfEraseSafe,
  createDynamicOpenBaselineEvaluator,
  DEFAULT_BASELINE_WINDOW_MS,
  DEFAULT_PERCENTILE_LOW,
} from "./dynamic-open-baseline";
import { createPoseResetMonitor } from "./pose-reset-monitor";
import { smileBlendshapeSuppressGate } from "./smile-blendshape-gate";
import { computeVgap2d, createVisionVadEvaluator } from "./vision-vad";
import { wideOpenSuppressGate } from "./wide-open-gate";

/**
 * `startFrameLoop` を session-page 用に wrap した detection loop adapter。
 *
 * 役割:
 * - vision-VAD evaluator (raw 2D MAR Δ 時系列) で effectiveIsSpeaking を判定し、 detection-machine へ渡す
 * - dynamic-open-baseline evaluator (rolling p10/p90) で raw 2D MAR を 0..1 にレンジ正規化する
 * - realtime signal 更新 + 永続化累積 push を 1 onResult に集約
 * - PiP / main window の rAF source 切替を `start(rafBindings)` メソッド 1 つに集約
 * - detection state を factory closure に閉じる
 *
 * vision-VAD signal source の変遷: `blendshapes.jawOpen` (ML noise) → raw 2D MAR ratio (F32、 但し分母
 * 口幅ジッタで TP-oblique 退行) → **vgap-2d-medfilt** (F41 本番反映、 issue#238)。 現行は内唇 P13/P14 の
 * 2D 距離 (= ratio の分母を外す) を path-length 前段で rolling median 平滑化し、 高周波ジッタを削り発話
 * エンベロープを残す。 F40 sweep が全軸 (TP 25/25 / oblique 15→18 / misdet FP 29→17 / 全 fps) で旧 default
 * を支配 (docs/eval-optimization-progress.md §F40 / §F41)。 dynamic-baseline は raw 2D MAR のまま
 * (窓のみ baselineWindowMs 8000 に拡張) = VAD と baseline で signal source が異なる。
 *
 * Provider 化は採用しない: detection state は frame-loop の onResult 単一 owner で、
 * 共有リソース要件を満たさない (issue #19 検討結果)。
 *
 * 30fps の 1 frame で main / worker / state を跨ぐデータフロー (この adapter が真ん中):
 *
 *   features/session/pages/session-page (orchestrator)
 *     └─ infra/frame-loop.startFrameLoop  ← rAF (PiP or main window)
 *           1. new VideoFrame(video)
 *           2. workerClient.call({ process-frame }, [videoFrame transferable])
 *                ↓ postMessage (transferable = zero-copy)
 *           ┌─ workers/analyzer.worker ────────────────────────────────┐
 *           │  landmarker.detectForVideo → landmarks                   │
 *           │  calculateMar(landmarks) → { mar2D, mar3D } (ADR 0004)   │
 *           │  extractBlendshapes(landmarks) → blendshapes (mouthSmile/cheekSquint) │
 *           │  videoFrame.close()  ← transferable は worker 側で必ず close │
 *           │  return AnalyzeFrameResult { mar, mar3D, faceDetected, ... } │
 *           └──────────────────────────────────────────────────────────┘
 *                ↑ postMessage (WorkerResult)
 *           3. onResult (= createDetectionResultHandler 以下):
 *                ├─ computeVgap2d(result.lipLandmarks) → vgap (内唇 P13/P14 の 2D 距離、 F41)
 *                ├─ visionVad.push(vgap, result.timestamp) → raw vgap ring 更新 (時刻 evict + medfilt margin)
 *                ├─ visionVad.evaluate() → effectiveIsSpeaking: boolean (medfilt → path長/秒 + latch)
 *                ├─ dynamicBaseline.push(result.mar, result.timestamp) → ring buffer 更新 (時刻 evict)
 *                ├─ dynamicBaseline.evaluate(result.mar) → effectiveMar (rolling p10/p90 で動的 normalize)
 *                ├─ smileBlendshapeSuppressGate(blendshapes) → suppressFire (ADR 0004 / F24、 issue#283 で B 層 reset 要因)
 *                ├─ detectionStep(state, input, tuning) ← features/session/lib/detection-machine
 *                │    └─ fire? → onFire → session-page.fireNotification
 *                ├─ pushSessionAccumulators(...) ← state/session-store (running 中だけ、 UC-14 累積 +
 *                │    issue #292 区間記録。 isOpen が step の openSince に依存するため step 後段)
 *                └─ updateRealtimeSignals({ sustainProgress }) ← step の openSince から導出 (issue #224、 30fps)
 *
 * ADR 0004 解消 (calibration UI 完全廃止 PR): production canonical な signal processing helper を
 * main 側で直列 compose する。 effectiveIsSpeaking = vgap-2d-medfilt Δ の path長/秒 (windowMs 窓 +
 * holdMs latch、 F41)、 effectiveMar = dynamic baseline (p10/p90) で正規化した値、 suppressFire =
 * mouthSmile tight 閾値 (+ ADR 0006 §軸 2 で rolling median 機能拡張) || wide-open gate。
 * detection-machine signature は同 ADR 0002 structure invariant 通り「pure 関数 (state, input,
 * tuning) → next」 を保つ。
 *
 * issue #302: 旧 helper `mar-3d-ratio` (mar3D/mar2D ratio で effectiveMar を deflate、 ADR 0004 / F24)
 * は、 役目 (idle-oblique 閉口 FP 抑止、 ADR 0009) が pose-reset-yaw (ADR 0013) に完全移行し現運転点で
 * inert 化したため除去した (census + ablation で flip 0 件 = 真の no-op、 §F53 / ADR 0009 Status)。
 * worker は `mar3D` を依然 raw signal として返す (eval substrate `mar-3d-v1` の replay で使用)。
 *
 * 規律は CLAUDE.md「設計原則」 の "worker = signal extractor、 main = stateful decision maker"
 * を参照。
 */

/**
 * detection-machine state + vision-VAD evaluator を factory closure に閉じる。
 * frame-loop の onResult として使う。 PiP rAF source 切替で frame-loop を再起動しても同じ closure を
 * 再利用できるよう、 起動関数 (`createDetectionLoop`) 内で 1 度だけ生成する。
 *
 * ADR 0006 (smile rolling median 軸): `SMILE_HISTORY_FRAMES=5` で production canonical で常時
 * rolling median を効かせる (= 1 frame 偽陽性 spike を majority rule で除外、 安全側設計)。
 * cycle-2 corpus 上は ref と完全同値 = TP / misdet 数値不変 (F33 sweep doc 参照)、 効果は別 corpus
 * (= 屋外 / rapid smile transition 等) で出る期待値。
 */
// ADR 0006 §F33 軸の適用記録 §軸 2: ARKit blendshape ML 推定 1 frame 偽陽性 spike を rolling median
// で外れ値除去する window size。 default `5` (= 直近 5 frame 中央値) で production canonical で常時
// 有効化。 cycle-2 で ref 同値だが本質的設計妥当性 (= 安全側) を ADR 0006 §決定 で根拠化。
const SMILE_HISTORY_FRAMES = 5;

export function createDetectionResultHandler(opts: {
  tuning: Accessor<DetectionTuning>;
  muted: Accessor<boolean>;
  onFire: (timestamp: number, normalizedMar: number) => void;
  /**
   * ADR 0016 D2 (grilling Q1/Q4): near-miss raw-capture の read-only 注入シーム。 確定済みの
   * raw frame / `DetectionInput` / `DetectionStep` を読むだけで、 `detectionState` 代入や `onFire`
   * 発火に一切触れない (US#9 = 観測が fire 出力を 1 件も変えない、 がコードの形として保証される)。
   * production composition root (session-page) は渡さない → 観測実装は treeshake で消え、 ここの
   * no-op hook 1 行だけが残る。 dev capture route (`/dev/near-miss-capture`) のみが wiring する。
   */
  observer?: (raw: AnalyzeFrameResult, input: DetectionInput, step: DetectionStep) => void;
}): (result: AnalyzeFrameResult) => void {
  let detectionState = INITIAL_STATE;
  // issue#230 #1b / #236 advisory: production の dynamic-baseline は default baselineWindowMs を使う。
  // sustainMs (tuning) と突き合わせ self-erase 不変条件 (= 連続開で normalizedMar が崩壊する前に sustain
  // 到達できる) を起動時に fail-fast。 default 8000 (F41) は (1−10/100)×8000=7200 > sustain 3000 で余裕が
  // あるが、 将来の default 変更の regression guard。 sustainMs は settings 非公開で初期 tuning 値で固定のため construction 時に 1 度検証。
  assertSelfEraseSafe(DEFAULT_BASELINE_WINDOW_MS, opts.tuning().sustainMs, DEFAULT_PERCENTILE_LOW);
  const visionVad = createVisionVadEvaluator();
  const dynamicBaseline = createDynamicOpenBaselineEvaluator();
  // pose-reset lever (issue #286 / ADR 0013): 頭部 yaw の大変化 (zone+d10 trigger) で baseline 窓を
  // 破棄し cold-start を再アームする。 正面で形成された p10 が旋回後も最大 ~7.2s 残存して斜め閉口に
  // +0.3 級の下駄を履かせる遷移 idle-oblique FP を断つ。 production 初の headPose 消費。
  const poseResetMonitor = createPoseResetMonitor();
  // pitch 軸 pose-reset (F52 lever A / ADR 0013 §Update 2026-06-12): pitch は **delta 単独**
  // (zoneEnabled=false)。 自然範囲の pitch 上げ (~24-32°) は zone enter 35° に届かず zone+d10 が
  // inert になるため (§F52 Q1)。 idle-pitch FP (= idle floor regime × pitch 起因 raw MAR 浮き) を
  // 窓 reset で断つ。 yaw monitor とは独立 instance (各自の delta 窓 / rearm debounce を持つ)。
  const pitchResetMonitor = createPoseResetMonitor({ zoneEnabled: false });
  // ADR 0006 §軸 2: smile rolling median 用 ring buffer (newest last、 current を含む)。
  // SMILE_HISTORY_FRAMES=5 で length 5 まで増えてから median 判定が始まる (= 4 frame までは
  // current だけで判定する fallback、 smile-blendshape-gate.ts §冒頭 cold-start 規律)。
  const recentBlendshapes: Array<Record<string, number> | undefined> = [];

  return (result) => {
    // VAD と動的 baseline は signal source が異なる (issue#238 F41):
    //   - VAD (発話抑止経路): vgap-2d (= 内唇 P13/P14 の 2D 距離) を medfilt 平滑化した path 長/秒。
    //     ratio の分母 (口幅) ジッタが TP-oblique を退行させる問題を断つため vgap に切替 (F40)。
    //   - dynamic-baseline (open-detection 正規化経路): raw 2D MAR (`result.mar`) のまま。 窓だけ
    //     baselineWindowMs=8000 に拡張 (oblique retention は baseline 側の問題、 F40 切り分け)。
    // 両 helper は各自の evict スパンを持つ (vision-vad=windowMs 3000 + medfilt margin /
    // dynamic-baseline=baselineWindowMs 8000)。 face-loss frame は両 helper の push で skip される
    // (vgap=undefined / rawMar=undefined の non-finite ガード)。
    const vgap = computeVgap2d(result.lipLandmarks, result.faceDetected);
    const rawMar = result.faceDetected ? result.mar : undefined;
    visionVad.push(vgap, result.timestamp);
    // pose-reset は同 frame の push より先に評価する (= reset 後の窓は現 frame の MAR から再形成、
    // eval twin `dynamic-open-baseline-v1` の poseReset replay と同順)。 headPose undefined
    // (face-loss) は monitor 側で状態保持 skip。 yaw / pitch は短絡させず両方 push する
    // (= 各 monitor の delta 窓を毎 frame 更新し続ける必要がある)。
    const yawTriggered = poseResetMonitor.push(result.headPose?.yaw, result.timestamp);
    const pitchTriggered = pitchResetMonitor.push(result.headPose?.pitch, result.timestamp);
    if (yawTriggered || pitchTriggered) {
      dynamicBaseline.reset(result.timestamp);
    }
    dynamicBaseline.push(rawMar, result.timestamp);
    const effectiveIsSpeaking = visionVad.evaluate();
    const dynamicEffective = dynamicBaseline.evaluate(result.mar);

    // effectiveMar = dynamic-open-baseline (rolling p10/p90) の正規化出力をそのまま使う。
    // 旧 mar-3d-ratio deflate (ADR 0004 / F24) は現運転点で inert 化したため issue #302 で除去
    // (§F53 / ADR 0009 Status)。 suppressFire = smile gate || wide-open gate。
    const effectiveMar = dynamicEffective;
    // ring buffer update: newest を末尾 push、 cap = SMILE_HISTORY_FRAMES。 historyFrames=1 のとき
    // length 1 に固定。
    recentBlendshapes.push(result.blendshapes);
    while (recentBlendshapes.length > SMILE_HISTORY_FRAMES) {
      recentBlendshapes.shift();
    }
    // suppressFire = smile gate || wide-open gate (F52 lever B / ADR 0014)。 どちらも「本物のぽかん
    // でない」 形状の B 層 reset 要因 (issue #283 セマンティクス) で OR 合流する。
    const suppressFire =
      smileBlendshapeSuppressGate({
        blendshapes: result.blendshapes,
        recentBlendshapes,
        historyFrames: SMILE_HISTORY_FRAMES,
      }) || wideOpenSuppressGate({ mar: result.mar, faceDetected: result.faceDetected });
    const detectionInput: DetectionInput = {
      effectiveMar,
      effectiveIsSpeaking,
      effectiveFaceDetected: result.faceDetected,
      suppressFire,
      muted: opts.muted(),
      timestamp: result.timestamp,
    };
    const stepResult = detectionStep(detectionState, detectionInput, opts.tuning());
    detectionState = stepResult.state;

    // ADR 0016 D2: read-only 観測シーム。 stepResult 確定後・onFire の前に、 確定済み
    // raw/input/state/fire を渡すだけ。 production は observer 未指定 = no-op。
    opts.observer?.(result, detectionInput, stepResult);

    // §issue #18 / #224 / #292: 永続化累積 push (UC-14 統計 + issue #292 区間記録)。 isOpen
    // (= 開口継続区間のエッジ源) が step() の openSince に依存するため detectionStep の後段に置く
    // (marPoint / speaking / unobserved の値は step と無関係なので順序の影響はない)。
    pushSessionAccumulators({
      mar: dynamicEffective,
      isSpeaking: effectiveIsSpeaking,
      isOpen: stepResult.state.openSince !== null,
      faceDetected: result.faceDetected,
      timestamp: result.timestamp,
    });

    // §issue #224 (段 3): signal 更新を detectionStep の後段に置くのは、 HUD 進捗が step() の
    // openSince に依存するため (= 発話 / 顔ロスト / cooldown 後の口閉じで step が openSince を reset
    // した結果を 0..1 の進捗にそのまま反映する)。 cooldown 中 (発火後も口開き継続) は openSince 維持で
    // 1.0 張り付き、 口を閉じると 0。 既知の罠: 口を完全静止で開け続けると dynamic-baseline 飽和で
    // effectiveMar が threshold を割り openSince が reset されうる (= 進捗が満タン手前で 0 に落ちる、
    // 実口の微動では起きにくい)。 sustainMs<=0 はゼロ除算回避ガード (既定 3000 で実害なし、 防御的)。
    const { openSince, lastFiredAt } = stepResult.state;
    const { sustainMs, cooldownMs } = opts.tuning();
    const sustainProgress =
      openSince === null || sustainMs <= 0
        ? 0
        : Math.max(0, Math.min(1, (result.timestamp - openSince) / sustainMs));
    // §issue #226: 質的表示 (見出し / 配色 / glow) は notifiedActive、 定量表示 (進捗バー fill) は
    // sustainProgress、 と role 分離する。 notifiedActive は通知発火〜cooldown の単一区間でのみ true で、
    // step() が発火時のみ更新する lastFiredAt に基づく (ミュート / suppressFire / cooldown 中は更新
    // されない = 実際に通知を出した区間と正確に一致)。 これで口元が threshold 付近を出入りしても
    // sustainProgress>0 の瞬間切替に依存していた質的表示が点滅しなくなる。
    const notifiedActive = lastFiredAt !== null && result.timestamp - lastFiredAt < cooldownMs;
    updateRealtimeSignals({ sustainProgress, notifiedActive });

    if (stepResult.fire) opts.onFire(result.timestamp, dynamicEffective);
  };
}

export type DetectionLoopHandle = {
  /**
   * 既存 loop の in-flight drain を await し、 さらに worker-handoff gate
   * (`worker.whenIdle()`) を通してから新しい raf source で起動する。 gate await 中に
   * `stop()` が割り込んだ場合は起動を中止する。
   */
  start: (rafBindings: RafBindings) => Promise<void>;
  /** frame-loop を停止 (in-flight drain Promise を含む)。 */
  stop: () => Promise<void>;
};

/**
 * `startFrameLoop` を wrap して PiP/main の rAF source 切替を `start(rafBindings)` メソッド 1 つに集約する。
 * detection-machine handler を factory closure に閉じる。
 */
export function createDetectionLoop(opts: {
  video: HTMLVideoElement;
  worker: WorkerClient;
  tuning: Accessor<DetectionTuning>;
  muted: Accessor<boolean>;
  onFps: (fps: number) => void;
  onFire: (timestamp: number, normalizedMar: number) => void;
  /** frame-loop の onError で fatal AppError が出た場合の通知先 (= handleStop)。 */
  onFatal: () => void;
}): DetectionLoopHandle {
  const onResult = createDetectionResultHandler({
    tuning: opts.tuning,
    muted: opts.muted,
    onFire: opts.onFire,
  });

  let stopCurrent: (() => Promise<void>) | null = null;
  // §issue #101 / U3 (AD1): start() は worker-handoff gate (`worker.whenIdle()`) を await
  // する。 その await 中に stop() (= teardown / PiP 切替) が割り込んだら起動を中止する
  // 必要がある — gen を stop() ごとに bump し、 start() は gate 解決後に自分の gen が
  // 最新かを再チェックする。
  let gen = 0;

  return {
    // §UX / §3.3 / §issue #101: 既存 loop の in-flight drain を await し、 さらに worker が
    // idle になる (= 同じ worker を使う前 loop の RPC が drain しきる) のを待ってから起動。
    // PiP 切替はいずれも同じ workerClient を再利用するため、
    // gate 無しで最初の process-frame を撃つと in-flight 1 件直列化に違反する
    // (WorkerRpcInFlight fatal)。
    start: async (rafBindings) => {
      const myGen = ++gen;
      const pending = stopCurrent;
      stopCurrent = null;
      await pending?.();
      // cross-instance handoff gate: 前 loop (PiP 経路で同一 instance) の in-flight RPC drain
      // を待つ。 worker idle なら即時。
      await opts.worker.whenIdle();
      // §AD1: 2 つの await (前 loop の drain / handoff gate) のいずれかの最中に stop() が
      // 割り込んでいたら startFrameLoop も最初の RPC も発火させない。 recheck は両 await の
      // 後・startFrameLoop の直前の 1 箇所で両方を捕捉する (handleStart の
      // signal.throwIfAborted() と対称な guard)。
      if (myGen !== gen) return;
      stopCurrent = startFrameLoop({
        video: opts.video,
        worker: opts.worker,
        ...rafBindings,
        onFps: opts.onFps,
        onResult,
        onError: (err) => {
          if (isAppError(err) && err.policy === "fatal") {
            handleAppError(err);
            opts.onFatal();
          }
        },
      });
    },
    stop: async () => {
      // gen を bump して、 gate await 中の start() があれば起動を中止させる (§AD1)。
      gen++;
      const pending = stopCurrent;
      stopCurrent = null;
      await pending?.();
    },
  };
}
